import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  ICreatePortfolioFormState,
  IPortfolioFormList,
} from '@/finsight/domain/vo/PortfolioMonitor/ICreatePortfolioFormState';
import { usePortfoliosContext } from '@/finsight/application/PortfolioMonitor/PortfoliosContext';
import { normalizePortfolioToFormState } from '@/finsight/application/PortfolioMonitor/PortfoliosContext/helpers';
import { usePortfolioMonitorContext } from '@/finsight/application/PortfolioMonitor/PortfolioMonitorContext';

export type TSetPayload = React.Dispatch<React.SetStateAction<ICreatePortfolioFormState>>;

export const useFormPayload = (isEditing) => {
  const { currencies: { collection } } = usePortfolioMonitorContext();
  const { selectedPortfolio, getPortfolioById } = usePortfoliosContext();
  const [payload, setPayload] = useState<ICreatePortfolioFormState>({});

  function getFirstListName(payload: { lists?: IPortfolioFormList[] }): string | null {
    const [firstListName = null] = payload?.lists?.map((list) => list.name) ?? [];
    return firstListName;
  }

  const [activeListName, setActiveListName] = useState<string | null>(getFirstListName(payload));

  useEffect(() => {
    const listNamesIncludesActive = payload?.lists?.some(({ name }) => name === activeListName);

    if (!activeListName || !listNamesIncludesActive) {
      setActiveListName(getFirstListName(payload));
    }
  }, [payload?.lists, activeListName]);

  const fetchSelectedPortfolio = async () => {
    if (isEditing && selectedPortfolio?.id) {
      const payload = await getPortfolioById(selectedPortfolio.id);
      setPayload(normalizePortfolioToFormState(payload, collection));
    }
  };

  useEffect(() => {
    fetchSelectedPortfolio();
  }, [selectedPortfolio?.id, isEditing]);

  return {
    payload,
    setPayload,
    activeListName,
    setActiveListName,
  };
};

type FormPayloadContextType = ReturnType<typeof useFormPayload>;

export const FormPayloadContext = createContext<FormPayloadContextType>(null);

export const useFormPayloadContext = () => {
  const context = useContext(FormPayloadContext);
  if (!context) {
    throw new Error('useFormPayloadContext must be used within the FormPayloadContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
  isEditing: boolean,
}

const FormPayloadContextProvider = ({ children, isEditing }: IProps) => (
  <FormPayloadContext.Provider value={ useFormPayload(isEditing) }>
    { children }
  </FormPayloadContext.Provider>
);

export default FormPayloadContextProvider;
